import React from 'react';
import { 
  Box, 
  Container, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Chip, 
  Button,
  Divider
} from '@mui/material';
import { 
  LocationOn, 
  Home, 
  TrendingUp, 
  AttachMoney,
  Star,
  Visibility,
  Schedule
} from '@mui/icons-material';

const FeaturedProperties = () => {
  const featuredProperties = [
    {
      id: 1,
      title: "Luxury Single Family Home - Newark",
      address: "123 Main Street, Newark, NJ 07102",
      upsetValue: "$145,000",
      estimatedValue: "$185,000",
      potentialROI: "27%",
      bedrooms: 4,
      bathrooms: 2,
      sqft: "2,100",
      yearBuilt: 2018,
      auctionDate: "December 15, 2024",
      status: "Featured",
      highlights: ["Recently Renovated", "Prime Location", "Low Reserve"]
    },
    {
      id: 2,
      title: "Investment Opportunity - Jersey City",
      address: "456 Oak Avenue, Jersey City, NJ 07305",
      upsetValue: "$89,500",
      estimatedValue: "$125,000",
      potentialROI: "40%",
      bedrooms: 3,
      bathrooms: 1,
      sqft: "1,450",
      yearBuilt: 2015,
      auctionDate: "December 18, 2024",
      status: "High Interest",
      highlights: ["Rental Ready", "Growing Area", "Below Market Value"]
    },
    {
      id: 3,
      title: "Fixer-Upper Potential - Camden",
      address: "789 Pine Street, Camden, NJ 08101",
      upsetValue: "$35,000",
      estimatedValue: "$75,000",
      potentialROI: "114%",
      bedrooms: 2,
      bathrooms: 1,
      sqft: "950",
      yearBuilt: 2010,
      auctionDate: "December 20, 2024",
      status: "Value Play",
      highlights: ["High Upside", "Needs TLC", "Quick Close"]
    }
  ];

  const getStatusColor = (status) => {
    switch (status) {
      case 'Featured': return 'warning';
      case 'High Interest': return 'error';
      case 'Value Play': return 'success';
      default: return 'default';
    }
  };

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: '#f8f9fa', py: 4 }}>
      <Container maxWidth="lg">
        {/* Header Section */}
        <Box sx={{ textAlign: 'center', mb: 6 }}>
          <Typography variant="h2" component="h1" sx={{ 
            fontWeight: 700, 
            color: '#1B5E20',
            mb: 2 
          }}>
            Featured Properties
          </Typography>
          <Typography variant="h6" sx={{ 
            color: '#666',
            maxWidth: '800px',
            mx: 'auto',
            lineHeight: 1.6
          }}>
            Discover hand-picked foreclosure properties with exceptional investment potential. 
            Our featured listings represent the best opportunities for maximum returns.
          </Typography>
        </Box>

        {/* Key Benefits */}
        <Card sx={{ mb: 6, bgcolor: '#e8f5e8' }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, color: '#1B5E20' }}>
              Why Choose Featured Properties?
            </Typography>
            <Grid container spacing={3}>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Star sx={{ color: '#1B5E20', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Curated Selection</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Each property is carefully reviewed by our investment experts for maximum potential.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <TrendingUp sx={{ color: '#1B5E20', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>High ROI Potential</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Properties with verified market data showing strong return opportunities.
                </Typography>
              </Grid>
              <Grid item xs={12} md={4}>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Visibility sx={{ color: '#1B5E20', mr: 1 }} />
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>Detailed Analysis</Typography>
                </Box>
                <Typography variant="body2" sx={{ color: '#666' }}>
                  Complete market analysis, comparable sales, and investment projections included.
                </Typography>
              </Grid>
            </Grid>
          </CardContent>
        </Card>

        {/* Featured Properties Grid */}
        <Grid container spacing={4}>
          {featuredProperties.map((property) => (
            <Grid item xs={12} md={6} lg={4} key={property.id}>
              <Card sx={{ 
                height: '100%',
                transition: 'transform 0.2s, box-shadow 0.2s',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
                }
              }}>
                <CardContent sx={{ p: 3 }}>
                  {/* Status Badge */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Chip 
                      label={property.status}
                      color={getStatusColor(property.status)}
                      size="small"
                      icon={<Star sx={{ fontSize: 16 }} />}
                    />
                    <Typography variant="caption" sx={{ color: '#666' }}>
                      ID: {property.id}
                    </Typography>
                  </Box>

                  {/* Property Title */}
                  <Typography variant="h6" sx={{ fontWeight: 600, mb: 1, color: '#1B5E20' }}>
                    {property.title}
                  </Typography>

                  {/* Address */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <LocationOn sx={{ fontSize: 16, color: '#666', mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      {property.address}
                    </Typography>
                  </Box>

                  {/* Financial Info */}
                  <Box sx={{ 
                    bgcolor: '#f5f5f5', 
                    borderRadius: 2, 
                    p: 2, 
                    mb: 2 
                  }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Upset Value
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#1B5E20' }}>
                          {property.upsetValue}
                        </Typography>
                      </Grid>
                      <Grid item xs={6}>
                        <Typography variant="caption" sx={{ color: '#666' }}>
                          Est. Value
                        </Typography>
                        <Typography variant="h6" sx={{ fontWeight: 600 }}>
                          {property.estimatedValue}
                        </Typography>
                      </Grid>
                    </Grid>
                    <Divider sx={{ my: 1 }} />
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography variant="caption" sx={{ color: '#666' }}>
                        Potential ROI
                      </Typography>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 700, 
                        color: '#2e7d32' 
                      }}>
                        {property.potentialROI}
                      </Typography>
                    </Box>
                  </Box>

                  {/* Property Details */}
                  <Grid container spacing={1} sx={{ mb: 2 }}>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Beds</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {property.bedrooms}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Baths</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {property.bathrooms}
                      </Typography>
                    </Grid>
                    <Grid item xs={4}>
                      <Typography variant="caption" sx={{ color: '#666' }}>Sq Ft</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 600 }}>
                        {property.sqft}
                      </Typography>
                    </Grid>
                  </Grid>

                  {/* Auction Date */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Schedule sx={{ fontSize: 16, color: '#666', mr: 0.5 }} />
                    <Typography variant="body2" sx={{ color: '#666' }}>
                      Auction: {property.auctionDate}
                    </Typography>
                  </Box>

                  {/* Highlights */}
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="caption" sx={{ color: '#666', mb: 1, display: 'block' }}>
                      Key Highlights
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {property.highlights.map((highlight, index) => (
                        <Chip 
                          key={index}
                          label={highlight} 
                          size="small" 
                          variant="outlined"
                          sx={{ fontSize: '0.7rem' }}
                        />
                      ))}
                    </Box>
                  </Box>

                  {/* Action Button */}
                  <Button 
                    variant="contained" 
                    fullWidth
                    sx={{ 
                      bgcolor: '#1B5E20',
                      '&:hover': { bgcolor: '#0d3f16' }
                    }}
                  >
                    View Full Details
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Call to Action */}
        <Box sx={{ 
          textAlign: 'center', 
          mt: 6, 
          bgcolor: '#1B5E20', 
          color: 'white',
          borderRadius: 3,
          p: 4
        }}>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 2 }}>
            Ready to Start Investing?
          </Typography>
          <Typography variant="h6" sx={{ mb: 3, opacity: 0.9 }}>
            Get access to our complete database of foreclosure properties
          </Typography>
          <Button 
            variant="contained" 
            size="large"
            sx={{ 
              bgcolor: 'white',
              color: '#1B5E20',
              fontWeight: 600,
              px: 4,
              '&:hover': { 
                bgcolor: '#f5f5f5' 
              }
            }}
          >
            Explore All Properties
          </Button>
        </Box>
      </Container>
    </Box>
  );
};

export default FeaturedProperties;














